/**
 * Enhanced stream manager with persistence, reconnection, and recovery
 */

import { getAuthToken } from './firebase';
import { logger } from '../utils/logger';
import { streamRecovery } from './streamRecovery';

// Stream state persistence keys
const STREAM_STATE_KEY = 'sse_stream_states';
const MESSAGE_STATUS_KEY = 'message_status_cache';

// Stream state interface
interface StreamState {
  conversationId: string;
  messageId: string | null;
  streamUrl: string;
  isActive: boolean;
  lastEventTime: number;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  backoffMultiplier: number;
}

interface MessageStatusCache {
  [messageId: string]: {
    status: 'streaming' | 'completed' | 'error';
    lastChecked: number;
    streamComplete: boolean;
  };
}

interface StreamManagerOptions {
  maxReconnectAttempts?: number;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  heartbeatIntervalMs?: number;
  statusPollIntervalMs?: number;
  streamTimeoutMs?: number;
}

export class StreamManager {
  private activeStreams = new Map<string, EventSource>();
  private streamStates = new Map<string, StreamState>();
  private heartbeatIntervals = new Map<string, NodeJS.Timeout>();
  private statusPollIntervals = new Map<string, NodeJS.Timeout>();
  private reconnectTimeouts = new Map<string, NodeJS.Timeout>();
  
  private options: Required<StreamManagerOptions>;

  constructor(options: StreamManagerOptions = {}) {
    this.options = {
      maxReconnectAttempts: options.maxReconnectAttempts ?? 5,
      initialBackoffMs: options.initialBackoffMs ?? 1000,
      maxBackoffMs: options.maxBackoffMs ?? 30000,
      heartbeatIntervalMs: options.heartbeatIntervalMs ?? 30000,
      statusPollIntervalMs: options.statusPollIntervalMs ?? 5000,
      streamTimeoutMs: options.streamTimeoutMs ?? 900000, // 15 minutes
    };

    // Load persisted stream states
    this.loadPersistedStates();
    
    // Start status polling for any persisted incomplete streams
    this.resumeIncompleteStreams();

    // Listen for page visibility changes to handle reconnection
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Listen for beforeunload to persist current states
    window.addEventListener('beforeunload', this.persistStates.bind(this));
  }

  /**
   * Create or resume a stream for a conversation
   */
  async createStream(
    conversationId: string, 
    streamUrl: string, 
    messageId: string | null = null
  ): Promise<void> {
    logger.debug('📡 StreamManager: Creating stream for conversation:', conversationId);

    // Close existing stream if any
    this.closeStream(conversationId);

    // Create stream state
    const streamState: StreamState = {
      conversationId,
      messageId,
      streamUrl,
      isActive: true,
      lastEventTime: Date.now(),
      reconnectAttempts: 0,
      maxReconnectAttempts: this.options.maxReconnectAttempts,
      backoffMultiplier: 1,
    };

    this.streamStates.set(conversationId, streamState);
    
    // Start the actual SSE connection
    await this.startSSEConnection(conversationId, streamState);
    
    // Persist state
    this.persistStates();
  }

  /**
   * Start the actual SSE connection
   */
  private async startSSEConnection(conversationId: string, streamState: StreamState): Promise<void> {
    const token = await getAuthToken();
    const { getApiBaseUrl } = await import('../utils/apiConfig');
    const apiBaseUrl = getApiBaseUrl();
    
    // Always prepend apiBaseUrl for relative paths (starts with /)
    // On AWS, frontend (app-aws.4klabs.ai) and API (api-aws.4klabs.ai) are on different origins
    const fullStreamUrl = streamState.streamUrl.startsWith('http')
      ? streamState.streamUrl
      : `${apiBaseUrl}${streamState.streamUrl}`;
    
    const authenticatedStreamUrl = token 
      ? `${fullStreamUrl}${fullStreamUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
      : fullStreamUrl;

    logger.debug('📡 StreamManager: Starting SSE connection:', authenticatedStreamUrl);

    const eventSource = new EventSource(authenticatedStreamUrl);
    this.activeStreams.set(conversationId, eventSource);

    // Set up event handlers
    this.setupEventHandlers(conversationId, eventSource, streamState);
    
    // Start heartbeat monitoring
    this.startHeartbeat(conversationId);
    
    // Start status polling as fallback
    if (streamState.messageId) {
      this.startStatusPolling(conversationId, streamState.messageId);
    }
    
    // Set stream timeout
    this.setStreamTimeout(conversationId);
  }

  /**
   * Set up all SSE event handlers
   */
  private setupEventHandlers(conversationId: string, eventSource: EventSource, streamState: StreamState): void {
    // Connection opened
    eventSource.onopen = () => {
      logger.debug('📡 StreamManager: Connection opened for conversation:', conversationId);
      console.log('🔌 EventSource state:', eventSource.readyState, '(0=CONNECTING, 1=OPEN, 2=CLOSED)');
      streamState.lastEventTime = Date.now();
      streamState.reconnectAttempts = 0;
      streamState.backoffMultiplier = 1;
      this.persistStates();
    };

    // Generic messages
    eventSource.onmessage = (event) => {
      this.handleGenericMessage(conversationId, event, streamState);
    };

    // Specific event types
    eventSource.addEventListener('message_start', (event) => {
      this.handleMessageStart(conversationId, event, streamState);
    });

    eventSource.addEventListener('message_chunk', (event) => {
      this.handleMessageChunk(conversationId, event, streamState);
    });

    eventSource.addEventListener('message_complete', (event) => {
      this.handleMessageComplete(conversationId, event, streamState);
    });

    eventSource.addEventListener('token_count_update', (event) => {
      this.handleTokenCountUpdate(conversationId, event, streamState);
    });

    eventSource.addEventListener('error', (event) => {
      this.handleErrorEvent(conversationId, event as MessageEvent, streamState);
    });

    eventSource.addEventListener('resources_updated', (event) => {
      this.handleResourcesUpdated(conversationId, event, streamState);
    });
    
    // Recovery events
    eventSource.addEventListener('recovery_status', (event) => {
      this.handleRecoveryStatus(conversationId, event, streamState);
    });
    
    eventSource.addEventListener('tool_failure', (event) => {
      this.handleToolFailure(conversationId, event, streamState);
    });

    // Connection errors
    eventSource.onerror = (error) => {
      console.log('❌ EventSource error - readyState:', eventSource.readyState, '(0=CONNECTING, 1=OPEN, 2=CLOSED)');
      this.handleConnectionError(conversationId, error, streamState);
    };

    // Forward other events to the original message service handlers
    this.forwardToOriginalHandlers(conversationId, eventSource, streamState);
  }

  /**
   * Handle generic SSE messages
   */
  private handleGenericMessage(_conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.debug('📡 StreamManager: Received generic message:', event);
    streamState.lastEventTime = Date.now();
    
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'connected') {
        logger.debug('✅ StreamManager: Connection confirmed');
      }
    } catch (error) {
      console.error('❌ StreamManager: Error parsing SSE message:', error);
    }
  }

  /**
   * Handle message_start event
   */
  private handleMessageStart(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.debug('📡 StreamManager: Received message_start event:', event);
    streamState.lastEventTime = Date.now();
    
    try {
      const data = JSON.parse(event.data);
      streamState.messageId = data.messageId;
      
      // Update message status cache
      this.updateMessageStatusCache(data.messageId, 'streaming', false);
      
      // Start status polling for this specific message
      this.startStatusPolling(conversationId, data.messageId);
      
      this.persistStates();
      
      // Forward event to original handlers
      window.dispatchEvent(new CustomEvent('stream-message_start', {
        detail: { conversationId, event, data: event.data }
      }));
    } catch (error) {
      console.error('❌ StreamManager: Error handling message_start:', error);
    }
  }

  /**
   * Handle message_chunk event
   */
  private handleMessageChunk(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    streamState.lastEventTime = Date.now();
    
    try {
      const data = JSON.parse(event.data);
      if (data.messageId && data.index) {
        console.log(`📝 StreamManager: Chunk ${data.index} received for message ${data.messageId.substring(0, 8)}... - EventSource state:`, this.activeStreams.get(conversationId)?.readyState);
      }
      if (data.messageId) {
        this.updateMessageStatusCache(data.messageId, 'streaming', false);
      }
      
      // Forward event to original handlers
      window.dispatchEvent(new CustomEvent('stream-message_chunk', {
        detail: { conversationId, event, data: event.data }
      }));
    } catch (error) {
      console.error('❌ StreamManager: Error handling message_chunk:', error);
    }
  }

  /**
   * Handle token_count_update event
   */
  private handleTokenCountUpdate(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    console.log('🎯 StreamManager: Received token_count_update SSE event');
    console.log('  Conversation ID:', conversationId);
    console.log('  Event data:', event.data);
    logger.debug('📊 StreamManager: Token count update:', event);
    streamState.lastEventTime = Date.now();
    
    try {
      const parsedData = JSON.parse(event.data);
      console.log('  Parsed data:', parsedData);
      
      // Forward event to update the message store
      console.log('  Dispatching stream-token_count_update event to window');
      window.dispatchEvent(new CustomEvent('stream-token_count_update', {
        detail: { conversationId, event, data: event.data }
      }));
      
    } catch (error) {
      console.error('❌ StreamManager: Error handling token_count_update:', error);
    }
  }

  /**
   * Handle message_complete event
   */
  private handleMessageComplete(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.info('✅ StreamManager: Message streaming completed:', event);
    streamState.lastEventTime = Date.now();

    try {
      const data = JSON.parse(event.data);
      if (data.messageId) {
        this.updateMessageStatusCache(data.messageId, 'completed', true);
        this.stopStatusPolling(conversationId);
      }

      // Forward event to original handlers BEFORE cleanup
      window.dispatchEvent(new CustomEvent('stream-message_complete', {
        detail: { conversationId, event, data: event.data }
      }));

      // If slides were updated during message processing, dispatch slide refresh event
      if (data.slidesUpdated) {
        logger.info('🎨 Slides updated during message processing, dispatching refresh event');
        window.dispatchEvent(new CustomEvent('slides-updated', {
          detail: {
            conversationId: conversationId,
            currentSlideVersions: data.currentSlideVersions || {},
            timestamp: data.timestamp || new Date().toISOString(),
            slideUpdateCount: data.metadata?.slideUpdateCount || 0
          }
        }));
      }

      // Mark stream as inactive and clean up
      streamState.isActive = false;
      this.cleanupStream(conversationId);

    } catch (error) {
      console.error('❌ StreamManager: Error handling message_complete:', error);
    }
  }

  /**
   * Handle resources_updated event
   */
  private handleResourcesUpdated(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.info('📁 StreamManager: Resources updated event:', event);
    streamState.lastEventTime = Date.now();
    
    try {
      const data = JSON.parse(event.data);
      
      // Dispatch custom event to notify ResourcesTab to refresh
      window.dispatchEvent(new CustomEvent('resources-updated', {
        detail: { 
          conversationId, 
          resources: data.resources,
          timestamp: data.timestamp 
        }
      }));
      
      logger.info(`✅ Dispatched resources-updated event for conversation ${conversationId}`);
    } catch (error) {
      console.error('❌ StreamManager: Error handling resources_updated:', error);
    }
  }
  
  /**
   * Handle recovery status events
   */
  private handleRecoveryStatus(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.info('🔄 StreamManager: Recovery status event:', event);
    streamState.lastEventTime = Date.now();
    
    try {
      const data = JSON.parse(event.data);
      
      // Use the recovery handler
      if (streamState.messageId) {
        streamRecovery.handleRecoveryStatus(streamState.messageId, {
          status: data.status,
          message: data.message,
          timestamp: data.timestamp
        });
      }
      
      // Also dispatch a custom event for any UI components that want to listen
      window.dispatchEvent(new CustomEvent('stream-recovery', {
        detail: {
          conversationId,
          messageId: streamState.messageId,
          status: data.status,
          message: data.message
        }
      }));
      
      logger.info(`🔄 Recovery status: ${data.status} - ${data.message}`);
    } catch (error) {
      console.error('❌ StreamManager: Error handling recovery_status:', error);
    }
  }
  
  /**
   * Handle tool failure events
   */
  private handleToolFailure(_conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.warn('⚠️ StreamManager: Tool failure event:', event);
    streamState.lastEventTime = Date.now();
    
    try {
      const data = JSON.parse(event.data);
      
      // Use the recovery handler
      streamRecovery.handleToolFailure({
        type: 'tool_failure',
        toolCallId: data.toolCallId,
        toolName: data.toolName,
        error: data.error,
        attemptedParameters: data.parameters,
        suggestions: data.suggestions
      });
      
      // Log for debugging with context
      logger.warn(`⚠️ Tool failure in conversation: ${data.toolName} - ${data.error}`);
      
      // The stream continues, so we don't close or error out
    } catch (error) {
      console.error('❌ StreamManager: Error handling tool_failure:', error);
    }
  }

  /**
   * Handle error events
   */
  private handleErrorEvent(conversationId: string, event: MessageEvent, streamState: StreamState): void {
    logger.error('❌ StreamManager: Received error event:', event);
    
    try {
      if (event.data) {
        JSON.parse(event.data); // Parse to validate, but don't use the data
      }
      
      if (streamState.messageId) {
        this.updateMessageStatusCache(streamState.messageId, 'error', true);
      }
      
      // Forward error event to message service
      window.dispatchEvent(new CustomEvent('stream-error', {
        detail: { conversationId, event, data: event.data, messageId: streamState.messageId }
      }));
      
      streamState.isActive = false;
      this.cleanupStream(conversationId);
      
    } catch (error) {
      console.error('❌ StreamManager: Error handling error event:', error);
    }
  }

  /**
   * Handle connection errors and attempt reconnection
   */
  private handleConnectionError(conversationId: string, error: Event, streamState: StreamState): void {
    logger.error('❌ StreamManager: Connection error for conversation:', conversationId, error);
    
    // Check if we should attempt reconnection
    if (streamState.reconnectAttempts < streamState.maxReconnectAttempts && streamState.isActive) {
      this.scheduleReconnection(conversationId, streamState);
    } else {
      logger.error('❌ StreamManager: Max reconnection attempts reached, giving up');
      streamState.isActive = false;
      this.cleanupStream(conversationId);
      
      // Start status polling as final fallback
      if (streamState.messageId) {
        this.startStatusPolling(conversationId, streamState.messageId);
      }
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnection(conversationId: string, streamState: StreamState): void {
    streamState.reconnectAttempts++;
    
    const backoffTime = Math.min(
      this.options.initialBackoffMs * Math.pow(2, streamState.reconnectAttempts - 1) * streamState.backoffMultiplier,
      this.options.maxBackoffMs
    );
    
    logger.debug(`📡 StreamManager: Scheduling reconnection ${streamState.reconnectAttempts}/${streamState.maxReconnectAttempts} in ${backoffTime}ms`);
    
    const timeoutId = setTimeout(async () => {
      if (streamState.isActive) {
        logger.debug('📡 StreamManager: Attempting reconnection for conversation:', conversationId);
        
        // Close current connection
        const currentStream = this.activeStreams.get(conversationId);
        if (currentStream) {
          currentStream.close();
          this.activeStreams.delete(conversationId);
        }
        
        // Attempt reconnection
        await this.startSSEConnection(conversationId, streamState);
      }
      
      this.reconnectTimeouts.delete(conversationId);
    }, backoffTime);
    
    this.reconnectTimeouts.set(conversationId, timeoutId);
  }

  /**
   * Start heartbeat monitoring for connection health
   */
  private startHeartbeat(conversationId: string): void {
    const intervalId = setInterval(() => {
      const streamState = this.streamStates.get(conversationId);
      const eventSource = this.activeStreams.get(conversationId);
      
      if (!streamState || !eventSource || !streamState.isActive) {
        this.stopHeartbeat(conversationId);
        return;
      }
      
      const timeSinceLastEvent = Date.now() - streamState.lastEventTime;
      
      // If no events for too long, consider connection stale
      if (timeSinceLastEvent > this.options.heartbeatIntervalMs * 2) {
        logger.warn('⚠️ StreamManager: Connection appears stale, forcing reconnection');
        this.handleConnectionError(conversationId, new Event('stale'), streamState);
      }
      
    }, this.options.heartbeatIntervalMs);
    
    this.heartbeatIntervals.set(conversationId, intervalId);
  }

  /**
   * Stop heartbeat monitoring
   */
  private stopHeartbeat(conversationId: string): void {
    const intervalId = this.heartbeatIntervals.get(conversationId);
    if (intervalId) {
      clearInterval(intervalId);
      this.heartbeatIntervals.delete(conversationId);
    }
  }

  /**
   * Start status polling as fallback mechanism
   */
  private startStatusPolling(conversationId: string, messageId: string): void {
    // Don't start multiple polling intervals for the same conversation
    if (this.statusPollIntervals.has(conversationId)) {
      return;
    }
    
    logger.debug('📊 StreamManager: Starting status polling for message:', messageId);
    
    const intervalId = setInterval(async () => {
      try {
        const messageStatus = await this.checkMessageStatus(messageId);
        
        if (messageStatus.streamComplete) {
          logger.info('✅ StreamManager: Status polling detected completion for message:', messageId);
          this.updateMessageStatusCache(messageId, 'completed', true);
          this.stopStatusPolling(conversationId);
          
          // Trigger completion handling in the original message service
          window.dispatchEvent(new CustomEvent('stream-recovery-complete', {
            detail: { conversationId, messageId, messageStatus }
          }));
        }
        
      } catch (error) {
        logger.error('❌ StreamManager: Status polling error:', error);
      }
    }, this.options.statusPollIntervalMs);
    
    this.statusPollIntervals.set(conversationId, intervalId);
  }

  /**
   * Stop status polling
   */
  private stopStatusPolling(conversationId: string): void {
    const intervalId = this.statusPollIntervals.get(conversationId);
    if (intervalId) {
      clearInterval(intervalId);
      this.statusPollIntervals.delete(conversationId);
    }
  }

  /**
   * Check message status via API
   */
  private async checkMessageStatus(messageId: string): Promise<{ streamComplete: boolean; status: string }> {
    // Import API dynamically to avoid circular dependencies
    const { default: api } = await import('./api');
    
    const response = await api.get(`/messages/${messageId}/status`);
    return response.data;
  }

  /**
   * Set stream timeout
   */
  private setStreamTimeout(conversationId: string): void {
    setTimeout(() => {
      const streamState = this.streamStates.get(conversationId);
      if (streamState && streamState.isActive) {
        logger.warn('⏰ StreamManager: Stream timeout reached, cleaning up');
        streamState.isActive = false;
        this.cleanupStream(conversationId);
        
        // Start status polling as final fallback
        if (streamState.messageId) {
          this.startStatusPolling(conversationId, streamState.messageId);
        }
      }
    }, this.options.streamTimeoutMs);
  }

  /**
   * Clean up stream resources
   */
  private cleanupStream(conversationId: string): void {
    logger.debug('🧹 StreamManager: Cleaning up stream for conversation:', conversationId);
    
    // Close EventSource
    const eventSource = this.activeStreams.get(conversationId);
    if (eventSource) {
      eventSource.close();
      this.activeStreams.delete(conversationId);
    }
    
    // Clear intervals and timeouts
    this.stopHeartbeat(conversationId);
    this.stopStatusPolling(conversationId);
    
    const reconnectTimeout = this.reconnectTimeouts.get(conversationId);
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      this.reconnectTimeouts.delete(conversationId);
    }
    
    // Remove stream state
    this.streamStates.delete(conversationId);
    
    // Persist updated states
    this.persistStates();
  }

  /**
   * Close stream for a conversation
   */
  closeStream(conversationId: string): void {
    const streamState = this.streamStates.get(conversationId);
    if (streamState) {
      streamState.isActive = false;
    }
    
    this.cleanupStream(conversationId);
  }

  /**
   * Close all active streams
   */
  closeAllStreams(): void {
    logger.debug('🔌 StreamManager: Closing all streams');
    
    for (const conversationId of this.streamStates.keys()) {
      this.closeStream(conversationId);
    }
  }

  /**
   * Get active stream conversations
   */
  getActiveStreams(): string[] {
    return Array.from(this.streamStates.keys()).filter(id => {
      const state = this.streamStates.get(id);
      return state?.isActive;
    });
  }

  /**
   * Check if conversation has active stream
   */
  hasActiveStream(conversationId: string): boolean {
    const streamState = this.streamStates.get(conversationId);
    return streamState?.isActive ?? false;
  }

  /**
   * Persist stream states to localStorage
   */
  private persistStates(): void {
    try {
      const statesToPersist = Array.from(this.streamStates.entries())
        .filter(([_, state]) => state.isActive)
        .map(([id, state]) => [id, { ...state, lastEventTime: Date.now() }]);
      
      localStorage.setItem(STREAM_STATE_KEY, JSON.stringify(statesToPersist));
    } catch (error) {
      logger.error('❌ StreamManager: Failed to persist states:', error);
    }
  }

  /**
   * Load persisted stream states from localStorage
   */
  private loadPersistedStates(): void {
    try {
      const persistedData = localStorage.getItem(STREAM_STATE_KEY);
      if (persistedData) {
        const states = JSON.parse(persistedData) as Array<[string, StreamState]>;
        
        // Only load recent states (within last 5 minutes)
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        
        for (const [conversationId, state] of states) {
          if (state.lastEventTime > fiveMinutesAgo && state.isActive) {
            this.streamStates.set(conversationId, state);
          }
        }
      }
    } catch (error) {
      logger.error('❌ StreamManager: Failed to load persisted states:', error);
    }
  }

  /**
   * Resume incomplete streams after page reload
   */
  private resumeIncompleteStreams(): void {
    for (const [conversationId, streamState] of this.streamStates.entries()) {
      if (streamState.isActive && streamState.messageId) {
        logger.debug('🔄 StreamManager: Resuming incomplete stream for conversation:', conversationId);
        
        // Start status polling for incomplete streams
        this.startStatusPolling(conversationId, streamState.messageId);
        
        // Optionally try to reconnect if stream URL is still valid
        // This might need API support to get fresh stream URLs
      }
    }
  }

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      // Page became visible, check for stale connections
      logger.debug('👁️ StreamManager: Page visible, checking stream health');
      
      for (const [conversationId, streamState] of this.streamStates.entries()) {
        if (streamState.isActive) {
          const eventSource = this.activeStreams.get(conversationId);
          if (!eventSource || eventSource.readyState === EventSource.CLOSED) {
            logger.debug('🔄 StreamManager: Reconnecting stale stream for conversation:', conversationId);
            this.scheduleReconnection(conversationId, streamState);
          }
        }
      }
    }
  }

  /**
   * Update message status cache
   */
  private updateMessageStatusCache(messageId: string, status: 'streaming' | 'completed' | 'error', streamComplete: boolean): void {
    try {
      const cache: MessageStatusCache = JSON.parse(localStorage.getItem(MESSAGE_STATUS_KEY) || '{}');
      cache[messageId] = {
        status,
        lastChecked: Date.now(),
        streamComplete,
      };
      localStorage.setItem(MESSAGE_STATUS_KEY, JSON.stringify(cache));
    } catch (error) {
      logger.error('❌ StreamManager: Failed to update message status cache:', error);
    }
  }

  /**
   * Forward events to original message service handlers
   */
  private forwardToOriginalHandlers(conversationId: string, eventSource: EventSource, streamState: StreamState): void {
    // This method allows the existing message service to handle events
    // while the stream manager handles connection reliability

    const eventTypes = [
      'tool_call_request',
      'tool_call_response',
      'tool_confirmation_required',
      'conversation_status',
      'thinking',
      'chat_compressed',
      'retry_status',
      'retry_countdown',
      'tool_status',
      'agent_activity'
    ];
    
    eventTypes.forEach(eventType => {
      eventSource.addEventListener(eventType, (event) => {
        streamState.lastEventTime = Date.now();
        
        logger.debug(`📡 StreamManager: Forwarding ${eventType} event for conversation:`, conversationId);
        
        // Dispatch custom event for original handlers to catch
        window.dispatchEvent(new CustomEvent(`stream-${eventType}`, {
          detail: { conversationId, event, data: event.data }
        }));
      });
    });
  }
}

// Create singleton instance
export const streamManager = new StreamManager();
export default streamManager;